import { useLazyRef, useUpdateEffect, χref } from '@studio/react-utils/hooks';
import { freeze } from 'immer';
import type { ReactElement } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { Context } from './context.js';
import type { Draft, Props, State, Store } from './types.js';
import { Panel, TopLevelPanel } from './types.js';

/** Account settings workflow `<Provider>`. */
export const Provider = ({
  account,
  children,
  defaultPanel,
  defaultTopLevelPanel,
  initialPlanPickerRoomId,
  hotKeys,
  //organizations,
  overrideOrganizationId,
  overrideRoomId,
  //rooms,
  user
}: Props): ReactElement => {
  const store = useLazyRef<Store>(() =>
    create<State>()(
      immer((set) =>
        freeze({
          [Panel.Billing]: { initialPlanPickerRoomId },
          [Panel.Controllers]: {},
          [Panel.HotKeys]: {
            hotKeys,
            isDefault: false,
            isDirty: false,
            snapshot: structuredClone(hotKeys)
          },
          [Panel.Notifications]: { isDirty: false },
          [Panel.Password]: { isDirty: false },
          [Panel.Pricing]: { isDirty: false },
          [Panel.Profile]: { isDirty: false, nextUser: user },
          [Panel.Organization]: { isDirty: false, overrideId: overrideOrganizationId },
          [Panel.Studio]: { isDirty: false, overrideId: overrideRoomId },
          topLevelPanel: defaultTopLevelPanel ?? TopLevelPanel.Account,
          panel: defaultPanel ?? Panel.Billing,
          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          }
        })
      )
    )
  );

  // Sync external status.
  useUpdateEffect(() => {
    const { produce } = χref(store).getState();

    produce((draft) => {
      draft[Panel.HotKeys] = {
        ...draft[Panel.HotKeys],
        hotKeys
      };

      draft[Panel.Organization] = {
        ...draft[Panel.Organization],
        overrideId: overrideOrganizationId
      };

      draft[Panel.Studio] = {
        ...draft[Panel.Studio],
        overrideId: overrideRoomId
      };
    });
  }, [account, hotKeys, user, overrideOrganizationId, overrideRoomId]);

  return <Context.Provider value={χref(store)}>{children}</Context.Provider>;
};
